import { tcp5000Door, tcp5000Listener } from './composeCreatePalette';
import type { CreateOutput, CreateSourceClass, ResolveOutputsInput } from './types';

function kongHasPath(routes: ResolveOutputsInput['kongRoutes'], needle: string): boolean {
  return (routes || []).some((route) =>
    (route.paths || []).some((path) => String(path).includes(needle))
  );
}

function mcpForApp(input: ResolveOutputsInput): { endpoint: string } | null {
  const appId = input.scopedAppId;
  if (!appId) return null;
  const row = (input.mcpEndpoints || []).find((item) => String(item.app_id || '') === appId);
  if (!row) return null;
  if (!row.mcp_provisioned || row.mcp_enabled === false) return null;
  const endpoint = String(row.mcp_endpoint || `/mcp/app/${appId}`).trim();
  return endpoint ? { endpoint } : null;
}

function ingestWritesTimescale(input: ResolveOutputsInput): boolean {
  const source = input.source;
  const storeMode = String(source?.store_mode || '').toLowerCase();
  const ingestMode = String(source?.ingest_mode || '').toLowerCase();
  if (storeMode === 'telemetry' || storeMode === 'api_telemetry' || storeMode === 'both') return true;
  if (ingestMode === 'timescale' || ingestMode === 'dual') return true;
  const volume = input.ops?.data_volume;
  if (volume && (volume.telemetry_rows != null || volume.api_telemetry_rows != null)) return true;
  return (input.pipelines || []).some((pipeline) => {
    const mode = String(pipeline.store_mode || '').toLowerCase();
    return mode === 'telemetry' || mode === 'api_telemetry' || mode === 'both';
  });
}

function ingestObserveStore(input: ResolveOutputsInput): string | null {
  const fromSource = String(input.source?.census_store || '').trim();
  if (fromSource) return fromSource;
  const fromVolume = String(input.ops?.data_volume?.census_store || '').trim();
  return fromVolume || null;
}

/**
 * Outputs the platform already has for this flow class.
 * Labels use the real store (Timescale, Observe, Expose, MCP, ingest.raw).
 * Missing doors stay empty with a why — no invented bus.
 */
export function resolveCreateOutputs(input: ResolveOutputsInput): CreateOutput[] {
  if (input.sourceClass === 'ingest') {
    return resolveIngestOutputs(input);
  }
  if (input.sourceClass === 'iot') {
    return resolveIotOutputs(input);
  }
  return [];
}

function resolveIngestOutputs(input: ResolveOutputsInput): CreateOutput[] {
  const outputs: CreateOutput[] = [];

  if (ingestWritesTimescale(input)) {
    outputs.push({
      id: 'out:timescale:measurements',
      store: 'Timescale',
      label: 'Timescale',
      path: 'iot_telemetry / api_telemetry',
      class: 'ingest',
      available: true,
      door: 'up',
    });
  }

  const observeStore = ingestObserveStore(input);
  if (observeStore) {
    outputs.push({
      id: 'out:observe:ingest-history',
      store: 'Observe',
      label: 'Observe',
      path: observeStore,
      class: 'ingest',
      available: true,
      door: 'up',
    });
  }

  if (kongHasPath(input.kongRoutes, '/public/api-ingest')) {
    outputs.push({
      id: 'out:expose:public-api-ingest',
      store: 'Expose',
      label: 'Expose',
      path: '/public/api-ingest',
      class: 'ingest',
      available: true,
      door: 'up',
    });
  }

  const mcp = mcpForApp(input);
  if (mcp) {
    outputs.push({
      id: `out:mcp:${input.scopedAppId}`,
      store: 'MCP',
      label: 'MCP',
      path: mcp.endpoint,
      class: 'ingest',
      available: true,
      door: 'up',
    });
  }

  if (outputs.length === 0) {
    outputs.push({
      id: 'out:ingest:empty',
      store: 'Observe',
      label: 'No output door',
      path: '',
      class: 'ingest',
      available: false,
      why: 'This ingest class has no Timescale write, Observe census, Expose /public/api-ingest route, or enabled MCP tool on this seat.',
      door: 'down',
      door_reason: 'No existing ingest output for this seat',
    });
  }

  return outputs;
}

function resolveIotOutputs(input: ResolveOutputsInput): CreateOutput[] {
  const outputs: CreateOutput[] = [];
  const listener = tcp5000Listener(input.listeners, input.scopedAppId);
  const door = tcp5000Door(listener);
  const deviceId = input.source?.device_id || input.source?.imei || null;

  outputs.push({
    id: 'out:iot:ingest-raw',
    store: 'ingest.raw',
    label: 'ingest.raw',
    path: 'ingest.raw.<tenant>',
    class: 'iot',
    available: true,
    door: door.door,
    door_reason: door.reason,
  });

  outputs.push({
    id: 'out:timescale:telemetry',
    store: 'Timescale',
    label: 'Timescale',
    path: deviceId ? `iot_telemetry (device_id ${deviceId})` : 'iot_telemetry (device_id)',
    class: 'iot',
    available: true,
    door: door.door,
    door_reason: door.reason,
  });

  return outputs;
}

export function emptyOutputWhy(sourceClass: CreateSourceClass): string {
  if (sourceClass === 'ingest') {
    return 'This ingest class has no Timescale write, Observe census, Expose /public/api-ingest route, or enabled MCP tool on this seat.';
  }
  return 'This IoT class has no ingest.raw or Timescale telemetry door on this seat.';
}

export function outputsForGraph(
  sourceClass: CreateSourceClass,
  input: Omit<ResolveOutputsInput, 'sourceClass'>
): CreateOutput[] {
  return resolveCreateOutputs({ ...input, sourceClass }).filter((item) => item.available);
}
